import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  TABS, fetchFeedPage, fetchPinned, fetchMyHeld, fetchLikedIds, toggleLike, compactCount,
  setDemoLikes,
} from '../../utils/reviews';
import Composer from './Composer';
import PostCard from './PostCard';
import './ReviewSection.css';

const EMPTY_COPY = {
  top: "Nobody's said anything yet. Be the first — make it wild.",
  recent: "Nobody's said anything yet. Be the first — make it wild.",
  restaurant: "The venue hasn't posted or replied yet.",
};

const postPath = (businessId, postId) => ['businesses', businessId, 'posts', postId];

// Everything people are saying about one restaurant: the composer, pinned
// prompts, the feed (Top by likes by default, Recent, or what the restaurant
// itself has posted or answered) and a filter to rated reviews only.
export default function ReviewSection({ restaurant, onRatingChanged }) {
  const { currentUid, user, isAdmin, ensureGuest, refreshGuestProfile } = useAuth();
  // DEMO TOOLS (admin only, pre-launch): post as a fresh generated user.
  const [asDemoUser, setAsDemoUser] = useState(false);
  const [tab, setTab] = useState('top');
  const [reviewsOnly, setReviewsOnly] = useState(false);
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pinned, setPinned] = useState([]);
  const [myHeld, setMyHeld] = useState([]);
  const [liked, setLiked] = useState(new Set());
  // Guards against a slow page landing after the tab has already changed.
  const requestRef = useRef(0);

  const businessId = restaurant.id;

  const loadLikes = useCallback(async (list) => {
    if (!currentUid || list.length === 0) return;
    const paths = Object.fromEntries(list.map((p) => [p.id, postPath(businessId, p.id)]));
    try {
      const ids = await fetchLikedIds(paths, currentUid);
      setLiked((prev) => new Set([...prev, ...ids]));
    } catch {
      // Hearts just stay unfilled; the feed itself already loaded.
    }
  }, [currentUid, businessId]);

  // First page whenever the tab or filter changes.
  useEffect(() => {
    const req = ++requestRef.current;
    setLoading(true);
    setLoadError(false);
    fetchFeedPage(businessId, tab, reviewsOnly, null)
      .then((page) => {
        if (req !== requestRef.current) return;
        setPosts(page.posts.filter((p) => !p.pinned));
        setCursor(page.cursor);
        setDone(page.done);
        loadLikes(page.posts);
      })
      .catch((err) => {
        if (req !== requestRef.current) return;
        console.warn('Could not load posts:', err.message);
        setPosts([]);
        setLoadError(true);
      })
      .finally(() => {
        if (req === requestRef.current) setLoading(false);
      });
  }, [businessId, tab, reviewsOnly, loadLikes]);

  useEffect(() => {
    fetchPinned(businessId).then((list) => { setPinned(list); loadLikes(list); }).catch(() => setPinned([]));
  }, [businessId, loadLikes]);

  useEffect(() => {
    fetchMyHeld(businessId, currentUid).then(setMyHeld).catch(() => setMyHeld([]));
  }, [businessId, currentUid]);

  useEffect(() => {
    if (!isAdmin) setAsDemoUser(false);
  }, [isAdmin]);

  async function loadMore() {
    if (!cursor || loading) return;
    const req = requestRef.current;
    setLoading(true);
    try {
      const page = await fetchFeedPage(businessId, tab, reviewsOnly, cursor);
      if (req !== requestRef.current) return;
      setPosts((prev) => [...prev, ...page.posts.filter((p) => !p.pinned && !prev.some((q) => q.id === p.id))]);
      setCursor(page.cursor);
      setDone(page.done);
      loadLikes(page.posts);
    } catch {
      setDone(true);
    } finally {
      setLoading(false);
    }
  }

  function handlePosted(post) {
    if (post.status === 'held') {
      setMyHeld((prev) => [post, ...prev]);
      return;
    }
    if (post.pinned) setPinned((prev) => [post, ...prev]);
    else setPosts((prev) => [post, ...prev.filter((p) => p.id !== post.id)]);
    // A new starred review can move the average and the count, and a new
    // rating from the same person turns their older review into a comment.
    if (post.rating != null) {
      onRatingChanged?.();
      setPosts((prev) => prev.map((p) => (p.id !== post.id && p.authorId === post.authorId && p.rating != null
        ? { ...p, rating: null, type: 'comment' } : p)));
    }
  }

  function handleDeleted(post) {
    const drop = (list) => list.filter((p) => p.id !== post.id);
    setPosts(drop);
    setPinned(drop);
    setMyHeld(drop);
    // Refreshes the header's post count, and the average if it had stars.
    if (post.status === 'visible') onRatingChanged?.();
  }

  // DEMO TOOLS: an admin setting a post's like count by hand.
  const handleSetLikes = isAdmin ? async (post, n) => {
    const res = await setDemoLikes({ businessId, postId: post.id, likeCount: n });
    const set = (list) => list.map((p) => (p.id === post.id ? { ...p, likeCount: res.likeCount } : p));
    setPosts(set);
    setPinned(set);
  } : null;

  // Optimistic: the heart fills immediately and the count moves, then the
  // server's answer settles it (or it snaps back on failure).
  async function handleToggleLike(post) {
    const was = liked.has(post.id);
    const apply = (on, delta) => {
      setLiked((prev) => { const next = new Set(prev); if (on) next.add(post.id); else next.delete(post.id); return next; });
      const bump = (list) => list.map((p) => (p.id === post.id ? { ...p, likeCount: Math.max(0, (p.likeCount || 0) + delta) } : p));
      setPosts(bump);
      setPinned(bump);
    };
    apply(!was, was ? -1 : 1);
    try {
      const asGuest = !user;
      await ensureGuest();
      const res = await toggleLike({ businessId, postId: post.id });
      const settle = (list) => list.map((p) => (p.id === post.id ? { ...p, likeCount: res.likeCount } : p));
      setPosts(settle);
      setPinned(settle);
      setLiked((prev) => { const next = new Set(prev); if (res.liked) next.add(post.id); else next.delete(post.id); return next; });
      if (asGuest) refreshGuestProfile();
    } catch {
      apply(was, was ? 1 : -1);
    }
  }

  const count = Number(restaurant.postCount) || 0;
  const heldIds = new Set(myHeld.map((p) => p.id));

  return (
    <section className="reviews" aria-labelledby="reviews-heading">
      <div className="reviews__head">
        <h2 id="reviews-heading" className="reviews__title">What people are saying</h2>
        {count > 0 && <span className="reviews__count">{compactCount(count)} {count === 1 ? 'post' : 'posts'}</span>}
      </div>

      {isAdmin && (
        <label className="reviews__postas">
          Posting as
          <select value={asDemoUser ? 'demo' : ''} onChange={(e) => setAsDemoUser(e.target.value === 'demo')}>
            <option value="">Myself</option>
            <option value="demo">A generated user (demo)</option>
          </select>
          <span className="reviews__postas-note">
            Demo: every post and reply gets a new made-up name. Clear it all under Admin → Users before launch.
          </span>
        </label>
      )}

      <Composer restaurant={restaurant} onPosted={handlePosted} asDemoUser={asDemoUser} />

      {pinned.length > 0 && (
        <div className="reviews__pinned">
          {pinned.map((p) => (
            <PostCard key={p.id} post={p} restaurant={restaurant} liked={liked.has(p.id)} onToggleLike={handleToggleLike} onDeleted={handleDeleted} onSetLikes={handleSetLikes} asDemoUser={asDemoUser} />
          ))}
        </div>
      )}

      <div className="reviews__controls">
        <div className="reviews__tabs" role="tablist" aria-label="Sort posts">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={tab === t.id ? 'active' : ''}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`reviews__chip${reviewsOnly ? ' reviews__chip--on' : ''}`}
          aria-pressed={reviewsOnly}
          onClick={() => setReviewsOnly((v) => !v)}
        >
          ★ Reviews only
        </button>
      </div>

      <div className="reviews__list" role="tabpanel">
        {myHeld.map((p) => (
          <PostCard key={p.id} post={p} restaurant={restaurant} liked={false} onToggleLike={() => {}} onDeleted={handleDeleted} />
        ))}
        {posts.filter((p) => !heldIds.has(p.id)).map((p) => (
          <PostCard key={p.id} post={p} restaurant={restaurant} liked={liked.has(p.id)} onToggleLike={handleToggleLike} onDeleted={handleDeleted} onSetLikes={handleSetLikes} asDemoUser={asDemoUser} />
        ))}

        {loading && <div className="reviews__loading"><div className="spinner" aria-label="Loading posts…" /></div>}
        {!loading && loadError && <p className="reviews__empty">Couldn't load posts right now. Refresh to try again.</p>}
        {!loading && !loadError && posts.length === 0 && myHeld.length === 0 && (
          <p className="reviews__empty">{reviewsOnly ? 'No rated reviews here yet.' : EMPTY_COPY[tab]}</p>
        )}
        {!loading && !done && posts.length > 0 && (
          <button type="button" className="reviews__more" onClick={loadMore}>Show more</button>
        )}
      </div>
    </section>
  );
}
